<template>
	<div class="hidden md:flex relative" ref="userMenuRef">
		<div
			@click="showMenu = !showMenu"
			class="flex items-center border-0 md:border md:border-dj-gray rounded-3xl w-[52px] md:w-[100px] h-[52px] mr-2 md:mr-0 cursor-pointer"
		>
			<BaseCloudImage
				v-if="useAuth.getUser().avatar && !useAuth.isUpdatingUser()"
				class="ml-[3px] rounded-full overflow-hidden"
				:public-id="useAuth.getUser().avatar"
				alt="Avatar"
				:transformation="[{ width: 44, height: 44, radius: 22, crop: 'thumb' }]"
			/>
			<div v-else class="flex justify-center items-center bg-gray-100 rounded-full ml-[3px] w-[44px] h-[44px]">
				<BaseIcon color="#969BA5" class="text-[24px] w-[24px] h-[24px]" icon="icon-park-outline:user" />
			</div>
			<BaseIcon class="hidden md:block ml-2" :width="30" :height="30" icon="ic:round-menu" />
		</div>
		<!-- list -->
		<div
			v-if="showMenu"
			class="fixed md:absolute top-0 md:top-[65px] right-0 w-[100%] md:w-[360px] py-6 md:py-8 px-4 md:px-6 h-[100vh] md:h-max md:border border-dj-gray md:rounded-big bg-[#F4F7FD] md:bg-white"
		>
			<div class="flex items-center">
				<BaseCloudImage
					v-if="useAuth.getUser().avatar"
					class="max-w-[44px] md:max-w-[60px] mr-5 rounded-full overflow-hidden"
					:public-id="useAuth.getUser().avatar"
					alt="Avatar"
					:transformation="[{ width: 60, height: 60, radius: 40, crop: 'thumb' }]"
				/>
				<div
					v-else
					class="flex justify-center items-center bg-gray-100 rounded-full mr-5 w-[44px] md:w-[60px] h-[44px] md:h-[60px]"
				>
					<BaseIcon color="#969BA5" class="text-[24px] w-[60%] h-[60%]" icon="icon-park-outline:user" />
				</div>

				<div class="flex flex-col justify-center h-full">
					<span class="font-medium text-lg md:text-xl">
						<template v-if="locale == 'hu'">
							{{ useAuth.getUser().vname }} {{ useAuth.getUser().kname }}
						</template>
						<template v-else>{{ useAuth.getUser().kname }} {{ useAuth.getUser().vname }}</template>
					</span>
					<span class="hidden md:block text-sm">{{ useAuth.getUser().email }}</span>
				</div>
				<button @click="showMenu = false" class="md:hidden absolute top-6 right-4">
					<BaseIcon class="" :width="36" :height="36" icon="ic:round-close" />
				</button>
			</div>
			<span class="block md:hidden text-sm mt-4">{{ useAuth.getUser().email }}</span>

			<ul
				class="flex flex-col pt-4 md:pt-6 mt-4 md:mt-6 border-0 md:border-t-[1px] border-dj-gray gap-y-6 md:gap-y-4"
			>
				<li class="flex items-center cursor-pointer" v-if="szmdVotingStatus">
					<NuxtLink
						class="flex items-center"
						:to="'/szmd/rating'"
						@click="
							showMenu = false;
							openSzmdBoxVoteModal();
						"
					>
						<BaseIcon color="#E1415A" class="mr-4 text-[24px]" icon="mdi:vote-outline" />
						<span class="text-dj-red">{{ $t("vote") }}</span>
					</NuxtLink>
				</li>
				<li class="flex items-center cursor-pointer">
					<NuxtLink class="flex items-center" :to="`/${locale}/saved-jobs`" @click="showMenu = false">
						<BaseIcon
							color="#969BA5"
							class="mr-4 text-[24px]"
							icon="material-symbols:shield-with-heart-outline-rounded"
						/>
						<span>{{ $t("user.savedJobs.title") }}</span>
					</NuxtLink>
				</li>
				<li class="flex items-center cursor-pointer">
					<NuxtLink class="flex items-center" :to="`/${locale}/my-applications`" @click="showMenu = false">
						<BaseIcon color="#969BA5" class="mr-4 text-[24px]" icon="prime:send" />
						<span>{{ $t("userMenu.applications") }}</span>
					</NuxtLink>
				</li>
				<li class="flex items-center cursor-pointer">
					<NuxtLink class="flex items-center" :to="`/${locale}/followed-companies`" @click="showMenu = false">
						<BaseIcon color="#969BA5" class="mr-4 text-[24px]" icon="carbon:location-company" />
						<span>{{ $t("user.followedCompanies.title") }}</span>
					</NuxtLink>
				</li>
				<li class="flex items-center cursor-pointer">
					<NuxtLink class="flex items-center" :to="`/${locale}/user/account`" @click="showMenu = false">
						<BaseIcon color="#969BA5" class="mr-4 text-[24px]" icon="icon-park-outline:user" />
						<span>{{ $t("userMenu.profile") }}</span>
					</NuxtLink>
				</li>
				<li class="flex items-center cursor-pointer" @click="logout">
					<BaseIcon color="#E1415A" class="mr-4 text-[24px]" icon="mdi:location-exit" />
					<span class="text-dj-red">{{ $t("userMenu.logout") }}</span>
				</li>
			</ul>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, inject } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "UserMenu",
	setup() {
		const { locale } = useI18n();

		const app = useNuxtApp();

		const userMenuRef = ref();
		const showMenu = ref(false);

		const region = ref("");
		const szmdVotingStatus = useSzmdVotingStatus();

		onClickOutside(userMenuRef, () => {
			showMenu.value = false;
		});

		function logout() {
			useAuth.logout();
		}

		const openSzmdBoxVoteModal = inject("openSzmdBoxVoteModal");

		return {
			userMenuRef,
			showMenu,
			logout,
			locale,
			region,
			szmdVotingStatus,
			openSzmdBoxVoteModal,
		};
	},
});
</script>
